/**
 * Image Context Validator
 *
 * Validates image URLs to ensure they come from trusted CDN domains.
 * This provides security by preventing arbitrary external URLs from being
 * used as reference images in the generation flow.
 */

/**
 * List of trusted CDN domains for image URLs.
 * URLs must have hostnames ending with one of these domains.
 */
export const TRUSTED_DOMAINS = ['pray.com', 'cloudfront.net', 'cloudinary.com', 's3.amazonaws.com'] as const;

/**
 * Validates that an image URL is from a trusted CDN domain.
 *
 * @param url - The URL to validate (can be undefined or null)
 * @returns true if the URL is from a trusted domain, false otherwise
 *
 * @example
 * isValidImageUrl('https://cdn.pray.com/photo.jpg') // true
 * isValidImageUrl('https://evil.com/photo.jpg') // false
 * isValidImageUrl(undefined) // false
 */
export function isValidImageUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }

    // Check if hostname ends with a trusted domain
    return TRUSTED_DOMAINS.some((domain) => {
      const hostname = parsed.hostname.toLowerCase();
      // Must either match exactly or be a subdomain (preceded by a dot)
      return hostname === domain || hostname.endsWith(`.${domain}`);
    });
  } catch {
    // URL parsing failed - invalid URL
    return false;
  }
}
